import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./ServicePages.css";

const brandingFeatures = [
  "Brand positioning and messaging frameworks",
  "Website visual systems for trust and clarity",
  "Campaign-ready design assets for social and ads",
  "Consistent color, typography, and voice standards",
  "Template and landing page branding alignment",
  "Creative direction tailored to your market segment"
];

const brandingFramework = [
  {
    title: "Positioning & Voice",
    detail:
      "We define how your brand should sound and what promise it should communicate to buyers, sellers, and referral partners."
  },
  {
    title: "Visual Identity System",
    detail:
      "We shape a consistent look through color, typography, imagery treatment, and layout patterns that feel premium and recognizable."
  },
  {
    title: "Website Experience Alignment",
    detail:
      "Your website structure, calls-to-action, and content hierarchy are tuned to your brand so trust and conversion feel seamless."
  },
  {
    title: "Campaign Consistency",
    detail:
      "We align social, landing pages, and promotional visuals so every touchpoint reinforces the same identity and quality level."
  }
];

const brandingDeliverables = [
  "Brand direction brief and positioning narrative",
  "Primary color and typography system",
  "Website visual style guidance",
  "CTA and messaging consistency recommendations",
  "Social and campaign design alignment checklist",
  "Brand implementation roadmap for growth"
];

const brandingFaq = [
  {
    q: "Is branding only for large brokerages?",
    a:
      "No. Agents, teams, and brokerages all benefit from strong branding because clarity and trust directly improve conversion rates."
  },
  {
    q: "Can branding help if I already have a logo?",
    a:
      "Yes. Branding is broader than logo design. We align message, visuals, and web experience so your identity works across channels."
  },
  {
    q: "Do you apply branding to templates too?",
    a:
      "Absolutely. We adapt template design language to your brand so your site feels custom and market-appropriate."
  },
  {
    q: "How does branding connect with SEO and social?",
    a:
      "Consistent brand systems improve engagement signals, content clarity, and campaign performance across search and social channels."
  }
];

export default function BrandingPage() {
  return (
    <>
      <Helmet>
        <title>Branding Services for Real Estate | Apto Websites</title>
        <meta
          name="description"
          content="Branding services by Apto Websites for agents, teams, and brokerages looking to stand out with stronger visual identity and strategic messaging."
        />
      </Helmet>
      <div className="service-page">
        <section className="service-hero">
          <div>
            <p className="service-kicker">Branding services</p>
            <h1>Branding That Makes Your Real Estate Business Instantly Recognizable</h1>
            <p>
              We combine strategy and creative direction to build a polished identity across your website,
              campaigns, and social channels.
            </p>
            <div className="service-quick-actions">
              <Link to="/pricing" className="service-btn primary">View Pricing</Link>
              <Link to="/message" className="service-btn secondary">Discuss Your Brand</Link>
            </div>
          </div>
          <img
            src={require("../../assets/imgs/agentpics/Big Blue House with People.jpg")}
            alt="Branding and visual identity for real estate"
          />
        </section>

        <section className="service-grid">
          {brandingFeatures.map((feature) => (
            <article className="service-card" key={feature}>
              <h3>{feature.split(" ").slice(0, 4).join(" ")}</h3>
              <p>{feature}</p>
            </article>
          ))}
        </section>

        <section className="service-detail-section">
          <header className="service-detail-header">
            <h2>Our Branding Framework</h2>
            <p>
              We approach branding as a growth system, not just a design refresh. Every visual and messaging choice is
              connected to trust, memorability, and lead conversion.
            </p>
          </header>
          <div className="service-columns">
            {brandingFramework.map((step) => (
              <article className="service-mini-card" key={step.title}>
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="service-detail-section">
          <div className="service-deliverables">
            <h3>What You Receive</h3>
            <ul>
              {brandingDeliverables.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="service-detail-section">
          <header className="service-detail-header">
            <h2>Branding Questions We Often Get</h2>
          </header>
          <div className="service-faq-grid">
            {brandingFaq.map((item) => (
              <article className="service-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="service-bottom-cta">
          <h2>Build a brand people remember and trust.</h2>
          <p>From visuals to messaging, we shape a high-converting identity for your market.</p>
          <div className="service-quick-actions">
            <Link to="/signup" className="service-btn primary">Start Branding</Link>
            <Link to="/templates" className="service-btn secondary">View Design Styles</Link>
          </div>
        </section>
      </div>
    </>
  );
}
